/**
 * Tiptap schema per toolbar, so the editor reads stored Quill 1 HTML and writes HTML Quill (and the API's allow-lists)
 * accept. The schema of a toolbar = its formats + the standard marks: a field never gains a format its toolbar cannot
 * make — the company `GeneralConditions` allow-list (`p br strong em u s ol ul li a`, WEB Task 3) holds for the
 * standard toolbar even for pasted content. Text of a dropped format is kept.
 */
import { Extension, Mark, type Extensions } from '@tiptap/vue-3'
import Placeholder from '@tiptap/extension-placeholder'
import StarterKit from '@tiptap/starter-kit'

import { isSafeHref, QUILL_CLASS } from '../sanitize'

export type RichTextToolbar = 'standard' | 'headings' | 'font'

export const QUILL_SIZES = ['small', 'large', 'huge'] as const
export const QUILL_FONTS = ['serif', 'monospace'] as const
export type QuillSize = (typeof QUILL_SIZES)[number]
export type QuillFont = (typeof QUILL_FONTS)[number]

const BLOCK_CLASS = /^ql-(?:align|indent|direction)-/

/** Quill's block classes (`ql-indent-1` on a list item, `ql-align-center` on a paragraph) survive a round trip. */
const QuillBlockClass = Extension.create({
  name: 'quillBlockClass',
  addGlobalAttributes() {
    return [
      {
        types: ['paragraph', 'heading', 'listItem'],
        attributes: {
          quillClass: {
            default: null,
            parseHTML: (element: HTMLElement) =>
              element.className
                .split(/\s+/)
                .filter((token) => BLOCK_CLASS.test(token) && QUILL_CLASS.test(token))
                .join(' ') || null,
            renderHTML: (attributes: { quillClass?: string | null }) =>
              attributes.quillClass ? { class: attributes.quillClass } : {},
          },
        },
      },
    ]
  },
})

function classMark(name: string, prefix: string, values: readonly string[]) {
  const pattern = new RegExp(`(?:^|\\s)${prefix}(${values.join('|')})(?:\\s|$)`)
  return Mark.create({
    name,
    addAttributes() {
      return {
        value: {
          default: null,
          parseHTML: (element: HTMLElement) => pattern.exec(element.className)?.[1] ?? null,
          renderHTML: (attributes: { value?: string | null }) =>
            attributes.value ? { class: `${prefix}${attributes.value}` } : {},
        },
      }
    },
    parseHTML() {
      // Quill puts several class formats on one span: both marks read it (`consuming: false`).
      return [
        {
          tag: 'span',
          consuming: false,
          getAttrs: (element: HTMLElement) => (pattern.test(element.className) ? null : false),
        },
      ]
    },
    renderHTML({ HTMLAttributes }) {
      return ['span', HTMLAttributes, 0]
    },
  })
}

/** `<span class="ql-size-large">` (message templates' size picker). */
export const QuillSizeMark = classMark('quillSize', 'ql-size-', QUILL_SIZES)
/** `<span class="ql-font-serif">` (message templates' font picker). */
export const QuillFontMark = classMark('quillFont', 'ql-font-', QUILL_FONTS)

function scriptMark(name: 'subscript' | 'superscript', tag: 'sub' | 'sup', other: string) {
  return Mark.create({
    name,
    excludes: other,
    parseHTML: () => [{ tag }],
    renderHTML: ({ HTMLAttributes }) => [tag, HTMLAttributes, 0],
  })
}

export const SubscriptMark = scriptMark('subscript', 'sub', 'superscript')
export const SuperscriptMark = scriptMark('superscript', 'sup', 'subscript')

export function richTextExtensions(toolbar: RichTextToolbar, placeholder: string): Extensions {
  const extensions: Extensions = [
    StarterKit.configure({
      heading: toolbar === 'headings' ? { levels: [1, 2, 3, 4, 5, 6] } : false,
      // Not in any legacy toolbar nor in the allow-lists.
      blockquote: false,
      code: false,
      codeBlock: false,
      horizontalRule: false,
      // An empty paragraph appended after a final list would change stored content on mere load.
      trailingNode: false,
      link: {
        openOnClick: false,
        // Quill did not link typed URLs (the API makes bare links clickable on save).
        autolink: false,
        defaultProtocol: 'https',
        isAllowedUri: (url) => isSafeHref(url),
        HTMLAttributes: { target: '_blank', rel: 'noopener noreferrer', class: null },
      },
    }),
    QuillBlockClass,
  ]
  if (toolbar === 'font') extensions.push(QuillSizeMark, QuillFontMark, SubscriptMark, SuperscriptMark)
  if (placeholder) extensions.push(Placeholder.configure({ placeholder }))
  return extensions
}
